import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
    Button,
    Grid,
    GridColumn,
    Heading,
    Modal,
    ModalContent,
    ModalFooter, Notice,
    ScrollFade, Spinner,
    Table,
    TableCell,
    TableRow,
    Text,
} from 'telia-front-react';
import TranslateStore from '@stores/TranslateStore';
import DisturbanceModalStore from '@stores/DisturbanceModalStore';
import config from '@config';
import { DisturbanceBusinessArea as businessArea, DisturbanceType } from '@config/DisturbanceConfig';
import Disturbance from './Disturbance';
import { formatClock, formatClockless } from '@util/DateUtil';

interface DisturbanceDataModalProps {
    translateStore?: TranslateStore;
    disturbanceModalStore?: DisturbanceModalStore;
}

/**
 * Render DisturbanceDataModal
 * @param props Required properties for DisturbanceDataModal
 * @constructor
 */

const DisturbanceDataModal = (props: DisturbanceDataModalProps) => {
    const {
        translateStore: { translate },
        disturbanceModalStore: { isLoading, modalIsOpen, disturbance, extraData, type, getDuration }
    } = props;

    return disturbance ? (
        <Modal
            hasContentComponent={true}
            isOpen={modalIsOpen}
            onClose={() => {
                props.disturbanceModalStore.closeModal();
            }}
        >
            <ModalContent>
                <Text>
                    <Heading element="h4" className="h3">ID: {disturbance.id}</Heading>
                    {isLoading ? (
                        <Spinner />
                    ) : !extraData ? (
                        <Notice type="error" direction="vertical">
                            <Button
                                text={translate('general.refresh')}
                                onClick={(event) => {
                                    event.preventDefault();
                                    props.disturbanceModalStore.fetchDisturbance(disturbance);
                                }}
                            />
                        </Notice>
                    ) : (
                        <Table>
                            <tbody>
                                {
                                    [
                                        DisturbanceType.ChangeDisturbanceInfo,
                                        DisturbanceType.IncidentDisturbanceInfo
                                    ].includes(type) && (
                                        <>
                                            <TableRow>
                                                <TableCell type="th" scope="row" valign="top">{translate('table.start')}</TableCell>
                                                <TableCell>{getTime(disturbance.start !== null && disturbance.start !== '' ? disturbance.start : disturbance.plannedStart)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell type="th" scope="row" valign="top">{translate('table.end')}</TableCell>
                                                <TableCell>{getTime(disturbance.end !== null && disturbance.end !== '' ? disturbance.end : disturbance.plannedEnd)}</TableCell>
                                            </TableRow>
                                        </>
                                    )
                                }
                                <TableRow>
                                    <TableCell type="th" scope="row" valign="top">{translate('disturbance.status')}</TableCell>
                                    <TableCell>{disturbance.status}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell type="th" scope="row" valign="top">{translate('disturbance.heading')}</TableCell>
                                    <TableCell>{disturbance.heading}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell type="th" scope="row" valign="top">{translate('disturbance.description')}</TableCell>
                                    <TableCell>{disturbance.description}</TableCell>
                                </TableRow>
                                {
                                  [
                                      DisturbanceType.ChangeDisturbanceInfo,
                                      DisturbanceType.IncidentDisturbanceInfo
                                  ].includes(type) && (
                                    <>
                                        <TableRow>
                                            <TableCell type="th" scope="row" valign="top">{translate('table.location')}</TableCell>
                                            <TableCell>
                                                <div style={{ maxHeight: 150 }}>
                                                    <ScrollFade variant="light">
                                                        {extraData.location && extraData.location.map(
                                                          (location, key) => (
                                                            <p key={key}>
                                                                {location.name}
                                                            </p>
                                                          )
                                                        )}
                                                    </ScrollFade>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                  )
                                }
                                {
                                    type === DisturbanceType.ChangeDisturbanceInfo && getDuration !== null ? (
                                        <TableRow>
                                            <TableCell type="th" scope="row" valign="top">{translate('disturbance.disruption')}</TableCell>
                                            <TableCell>{
                                                getDuration
                                            }</TableCell>
                                        </TableRow>
                                    ) : <></>
                                }
                                {
                                    [
                                        DisturbanceType.ChangeDisturbanceInfo,
                                        DisturbanceType.IncidentDisturbanceInfo
                                    ].includes(type) && (
                                        <>
                                            <TableRow>
                                                <TableCell type="th" scope="row" valign="top" width={2}>{translate('disturbance.related.entity')}</TableCell>
                                                <TableCell>
                                                    <div style={{ maxHeight: 150 }}>
                                                        <ScrollFade variant="light">
                                                            {extraData.relatedEntity && extraData.relatedEntity.map(
                                                                (entity, key) => (
                                                                    <p key={key}>
                                                                        {entity.name}, {entity.siaId} {entity.address && '(' + entity.address + ')'}
                                                                    </p>
                                                                )
                                                            )}
                                                        </ScrollFade>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell type="th" scope="row" valign="top" width={2}>{translate('disturbance.devices')}</TableCell>
                                                <TableCell>
                                                    <div style={{ maxHeight: 150 }}>
                                                        <ScrollFade variant="light">
                                                            {extraData.devices && extraData.devices.map((device, key) => (
                                                                <p key={key}>
                                                                    {device.refDeviceId} {
                                                                        device.refDeviceManufacturer &&
                                                                        device.refDeviceModel &&
                                                                        '(' + device.refDeviceId + ', ' + device.refDeviceModel + ')'
                                                                    }
                                                                </p>
                                                            ))}
                                                        </ScrollFade>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )
                                }
                                {
                                    [
                                        DisturbanceType.ChangeDisturbanceInfo,
                                        DisturbanceType.IncidentDisturbanceInfo,
                                        DisturbanceType.ProblemDisturbanceInfo
                                    ].includes(type) && (
                                        <>
                                            {disturbance.id && (
                                                <TableRow>
                                                    <TableCell type="th" scope="row" valign="top">{translate('disturbance.sm')}</TableCell>
                                                    <TableCell>
                                                        {validateSm(type, disturbance.id) ? (
                                                            <Button
                                                                text={translate('disturbance.link.click')}
                                                                link={true}
                                                                url={getSmUrl(type, disturbance.id)}
                                                                target="_blank"
                                                                type="employee"
                                                            />
                                                        ) : (
                                                            disturbance.id
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    )
                                }
                                {
                                    type === DisturbanceType.IncidentDisturbanceInfo && (
                                        <TableRow>
                                            <TableCell type="th" scope="row" valign="top">{translate('disturbance.workaround')}</TableCell>
                                            <TableCell>{disturbance.workaround}</TableCell>
                                        </TableRow>
                                    )
                                }
                                {
                                    [
                                        DisturbanceType.IncidentDisturbanceInfo,
                                        DisturbanceType.ProblemDisturbanceInfo
                                    ].includes(type) && (
                                        <>
                                            <TableRow>
                                                <TableCell type="th" scope="row" valign="top">{translate('disturbance.resolution')}</TableCell>
                                                <TableCell>{extraData.resolution}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell type="th" scope="row" valign="top">
                                                    {type === DisturbanceType.IncidentDisturbanceInfo ?
                                                        translate('disturbance.cause.incident') :
                                                        translate('disturbance.cause.problem')
                                                    }
                                                </TableCell>
                                                <TableCell>{extraData.primaryCause}</TableCell>
                                            </TableRow>
                                        </>
                                    )
                                }
                            </tbody>
                        </Table>
                    )}
                </Text>
            </ModalContent>
            <ModalFooter>
                <Grid align={['end-xs']}>
                    <GridColumn width={['xs']}>
                        <Button
                            text={translate('general.close')}
                            type="employee"
                            onClick={(event) => {
                                event.preventDefault();
                                props.disturbanceModalStore.closeModal();
                            }}
                        />
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    ) : (
        <></>
    );
}

/**
 * Analyze conditions and decide if SM url is possible or not
 * @param type Disturbance @type
 * @param id Disturbance informationalId
 * @return boolean - If conditions are valid for SM url
 */
function validateSm(type: DisturbanceType, id: string) {
    const prefix = id.substring(0, 2);
    if (type === DisturbanceType.ChangeDisturbanceInfo && prefix === 'CM') {
        return true;
    } else if (type === DisturbanceType.IncidentDisturbanceInfo && prefix === 'IM') {
        return true;
    } else if (type === DisturbanceType.ProblemDisturbanceInfo && prefix === 'PM') {
        return true;
    }
    return false;
}

function getTime(date: string): JSX.Element {
    return date ? (
        <span>{formatClockless(date)} <span className="text-light">|</span> {formatClock(date)}</span>
    ) : null;
}

function getSmUrl(type: DisturbanceType, id: string) {
    if (type === DisturbanceType.ChangeDisturbanceInfo) {
        return `${config.smUrl}&file=cm3r&query=number="${id}"`;
    } else if (type === DisturbanceType.IncidentDisturbanceInfo) {
        return `${config.smUrl}&file=probsummary&query=number="${id}"`;
    } else {
        return `${config.smUrl}&file=rootcause&query=id="${id}"`;
    }
}

export default inject('translateStore', 'disturbanceModalStore')(
    observer(DisturbanceDataModal)
);